import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon, type IconName } from "@/components/ui/Icon/Icon";
import { CreateBrandModal } from "@/components/app/CreateBrandModal";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useBrands,
  useWorkspace,
  useWorkspaces,
  useTrendDigests,
  type BrandSummary,
  type CompGrant,
  type Workspace,
  type WorkspaceRole,
} from "@/lib/queries";
import {
  getLastBrandId,
  setLastBrandId,
  setLastWorkspaceId,
} from "@/lib/workspacePref";
import { compStatus } from "@notamen/core/workspace";
import { subscriptionStatus, type SubscriptionSummary } from "@notamen/core/billing";
import styles from "./AppShell.module.css";

/**
 * Humanize a positive millisecond span for the active-comp banner: days when
 * ≥1 day ("5 days"), else hours ("3 hours"), else minutes ("12 minutes"),
 * else "<1 minute". Mirrors the admin console's coarse countdown wording.
 */
function humanizeRemaining(ms: number): string {
  if (ms <= 0) return "<1 minute";
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  if (days >= 1) return days === 1 ? "1 day" : `${days} days`;
  const hours = Math.floor(totalMin / 60);
  if (hours >= 1) return hours === 1 ? "1 hour" : `${hours} hours`;
  const mins = totalMin % 60;
  if (mins >= 1) return mins === 1 ? "1 minute" : `${mins} minutes`;
  return "<1 minute";
}

/** "demo" → "Demo", "trial" → "Trial" for human-facing copy. */
function compTypeLabel(type: "demo" | "trial"): string {
  return type === "demo" ? "Demo" : "Trial";
}

/** Two-letter initials for a brand avatar, derived from the brand name. */
function brandInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

interface AppShellProps {
  /** The workspace currently in view, to highlight in the switcher. */
  currentWorkspaceId?: string | undefined;
  children: ReactNode;
}

/**
 * Authenticated app chrome: brand mark, workspace switcher, account
 * menu. Wraps every signed-in product surface. Auth gating is handled
 * by the routes themselves (they redirect to /signin); this assumes an
 * authenticated user.
 */
export function AppShell({ currentWorkspaceId, children }: AppShellProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const workspacesQuery = useWorkspaces();
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [acctMenuOpen, setAcctMenuOpen] = useState(false);
  // Shared by the sidebar "+" and the top-bar brand dropdown's "New brand".
  const [createBrandOpen, setCreateBrandOpen] = useState(false);
  const location = useLocation();
  const params = useParams<{ ws?: string; brand?: string }>();
  const navWs = params.ws ?? currentWorkspaceId;
  const navBrand = params.brand;
  const brandQuery = useBrand(navWs, navBrand);
  const brandName = brandQuery.data?.brand?.name;

  // Brand list + role for the chrome (top-bar dropdown + sidebar group).
  const brandsQuery = useBrands(navWs);
  const brands = brandsQuery.data?.brands ?? [];
  const workspaceQuery = useWorkspace(navWs);
  const role = workspaceQuery.data?.role;
  const canManageBrands = role === "owner" || role === "admin";

  // Access enforcement inputs. `comp` is the admin demo/trial grant;
  // `subscription` is the self-serve Stripe summary. Either being locked
  // (expired comp, or a canceled subscription) walls the workspace.
  const comp = workspaceQuery.data?.workspace?.comp;
  const subscription = workspaceQuery.data?.workspace?.subscription;

  // Remember the brand the user is actually viewing, so workspace-level
  // pages (Members, Settings) can keep the brand-scoped nav anchored to it
  // instead of collapsing the whole menu.
  useEffect(() => {
    if (navWs && navBrand) setLastBrandId(navWs, navBrand);
  }, [navWs, navBrand]);

  // The brand the sidebar's brand-scoped section points at: the one in the
  // URL, else the last one viewed in this workspace (if it still exists),
  // else the first brand. Keeps the nav stable across workspace-level pages
  // — the menu no longer changes when you open Members/Settings. Undefined
  // only when the workspace genuinely has no brands yet.
  const remembered = navWs ? getLastBrandId(navWs) : null;
  const activeBrand =
    navBrand ??
    (remembered && brands.some((b) => b.id === remembered) ? remembered : undefined) ??
    brands[0]?.id;
  const activeBrandName =
    activeBrand === navBrand
      ? brandName
      : brands.find((b) => b.id === activeBrand)?.name;

  const workspaces = workspacesQuery.data?.workspaces ?? [];
  const current = workspaces.find((w) => w.id === currentWorkspaceId);

  function goToWorkspace(ws: Workspace) {
    setLastWorkspaceId(ws.id);
    setWsMenuOpen(false);
    navigate(`/w/${ws.id}`);
  }

  function goToBrand(brandId: string) {
    setBrandMenuOpen(false);
    if (navWs) navigate(`/w/${navWs}/b/${brandId}`);
  }

  function openCreateBrand() {
    setBrandMenuOpen(false);
    setCreateBrandOpen(true);
  }

  return (
    <div className={styles.root}>
      <header className={styles.bar}>
        <Link to="/app" className={styles.brand}>
          <span className={styles.mark} aria-hidden="true">✻</span>
          <span className={styles.brandName}>Notamen</span>
        </Link>

        {/* Workspace switcher */}
        <div className={styles.switcherWrap}>
          <button
            className={styles.switcher}
            onClick={() => setWsMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={wsMenuOpen}
          >
            <span className={styles.switcherLabel}>
              {current?.name ?? "Select workspace"}
            </span>
            <Icon name="arrow-right" size={12} className={styles.switcherChevron} />
          </button>
          {wsMenuOpen && (
            <>
              <div className={styles.menuScrim} onClick={() => setWsMenuOpen(false)} />
              <div className={styles.menu} role="menu">
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    className={`${styles.menuItem} ${ws.id === currentWorkspaceId ? styles.menuItemActive : ""}`}
                    onClick={() => goToWorkspace(ws)}
                    role="menuitem"
                  >
                    <span>{ws.name}</span>
                    {ws.id === currentWorkspaceId && <Icon name="check" size={13} />}
                  </button>
                ))}
                <Link to="/app/new-workspace" className={styles.menuItemMuted} role="menuitem">
                  <Icon name="zap" size={13} />
                  New workspace
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Brand switcher (breadcrumb: Workspace ▾ / Brand ▾). Only shown
            once a workspace is in context — the workspace owns the brands. */}
        {navWs && (
          <div className={styles.crumbSep} aria-hidden="true">
            /
          </div>
        )}
        {navWs && (
          <div className={styles.switcherWrap}>
            <button
              className={styles.switcher}
              onClick={() => setBrandMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={brandMenuOpen}
            >
              {navBrand && brandName && (
                <span className={styles.brandAvatarSm} aria-hidden="true">
                  {brandInitials(brandName)}
                </span>
              )}
              <span className={styles.switcherLabel}>
                {navBrand ? brandName ?? "Brand" : "Select brand"}
              </span>
              <Icon name="arrow-right" size={12} className={styles.switcherChevron} />
            </button>
            {brandMenuOpen && (
              <>
                <div className={styles.menuScrim} onClick={() => setBrandMenuOpen(false)} />
                <div className={styles.menu} role="menu">
                  {brands.length === 0 && (
                    <div className={styles.menuEmpty}>No brands yet.</div>
                  )}
                  {brands.map((b) => (
                    <button
                      key={b.id}
                      className={`${styles.menuItem} ${b.id === navBrand ? styles.menuItemActive : ""}`}
                      onClick={() => goToBrand(b.id)}
                      role="menuitem"
                    >
                      <span className={styles.menuItemLead}>
                        <span className={styles.brandAvatarSm} aria-hidden="true">
                          {brandInitials(b.name)}
                        </span>
                        <span className={styles.menuItemName}>{b.name}</span>
                      </span>
                      {b.id === navBrand && <Icon name="check" size={13} />}
                    </button>
                  ))}
                  {canManageBrands && (
                    <button
                      className={styles.menuItemMuted}
                      onClick={openCreateBrand}
                      role="menuitem"
                    >
                      <span className={styles.plusGlyph} aria-hidden="true">
                        +
                      </span>
                      New brand
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className={styles.spacer} />

        {/* Account menu */}
        <div className={styles.acctWrap}>
          <button
            className={styles.acct}
            onClick={() => setAcctMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={acctMenuOpen}
          >
            <span className={styles.avatar} aria-hidden="true">
              {(auth.user?.email ?? "?").charAt(0).toUpperCase()}
            </span>
          </button>
          {acctMenuOpen && (
            <>
              <div className={styles.menuScrim} onClick={() => setAcctMenuOpen(false)} />
              <div className={`${styles.menu} ${styles.menuRight}`} role="menu">
                <div className={styles.acctEmail}>{auth.user?.email}</div>
                <Link
                  to="/account#2fa"
                  className={styles.menuItemMuted}
                  role="menuitem"
                >
                  <Icon name="smartphone" size={13} />
                  Two-factor authentication
                </Link>
                {auth.user?.isAdmin && (
                  <Link to="/admin" className={styles.menuItemMuted} role="menuitem">
                    <Icon name="lock" size={13} />
                    Admin · console
                  </Link>
                )}
                {auth.user?.isAdmin && (
                  <Link to="/admin/waitlist" className={styles.menuItemMuted} role="menuitem">
                    <Icon name="bell" size={13} />
                    Admin · waitlist
                  </Link>
                )}
                <button
                  className={styles.menuItemMuted}
                  onClick={() => auth.signOut()}
                  role="menuitem"
                >
                  <Icon name="arrow-left" size={13} />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {navWs ? (
        <div className={styles.body}>
          <SideNav
            ws={navWs}
            brand={navBrand}
            activeBrand={activeBrand}
            activeBrandName={activeBrandName}
            pathname={location.pathname}
            brands={brands}
            canManageBrands={canManageBrands}
            onCreateBrand={() => setCreateBrandOpen(true)}
          />
          <main className={styles.content}>
            <CompGate
              ws={navWs}
              comp={comp}
              subscription={subscription}
              role={role}
              pathname={location.pathname}
            >
              {children}
            </CompGate>
          </main>
        </div>
      ) : (
        <main className={styles.content}>{children}</main>
      )}

      {navWs && (
        <CreateBrandModal
          wsId={navWs}
          open={createBrandOpen}
          onClose={() => setCreateBrandOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Access enforcement surface. Walls the workspace when EITHER an admin
 * demo/trial grant has expired (`compStatus(comp).locked`) OR a self-serve
 * subscription has been canceled (`subscriptionStatus(subscription).locked`):
 *   - locked  → a full-page wall replaces the product UI. Owners get a
 *               "Subscribe to continue" CTA to the in-app billing page (which
 *               stays reachable while locked); non-owners are told to ask an
 *               owner. The wall copy reflects which lock fired.
 *   - active comp → a slim live banner counts down to expiry (60s tick).
 *   - none    → renders children unchanged.
 * Mirrors the server gate in the API (comp-lock OR subscription-lock). A
 * never-subscribed / no-grant workspace is never locked (both predicates
 * return locked:false on undefined).
 */
function CompGate({
  ws,
  comp,
  subscription,
  role,
  pathname,
  children,
}: {
  ws: string | undefined;
  comp: CompGrant | undefined;
  subscription: SubscriptionSummary | undefined;
  role: WorkspaceRole | undefined;
  pathname: string;
  children: ReactNode;
}) {
  // The billing page is the escape hatch out of a lock — the API keeps its
  // GET + subscribe routes reachable while locked, so we must NOT wall the
  // billing page itself, or a locked owner could never reach the switcher to
  // re-subscribe. Render it through the wall.
  const onBillingPage = !!ws && pathname === `/w/${ws}/billing`;
  // Re-derive once a minute so the comp banner countdown stays live and an
  // active grant flips to the wall the moment it expires mid-session, without
  // waiting for a refetch. (Subscription lock is server-driven, not time-based,
  // so it only needs the comp timer.)
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!comp) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [comp]);

  const cs = compStatus(comp, now);
  const subLocked = subscriptionStatus(subscription).locked;
  const isOwner = role === "owner";

  // Either lock walls the workspace — UNLESS we're on the billing page (the
  // re-subscribe escape hatch). Comp wording wins when it's the comp that
  // expired; otherwise it's a canceled subscription.
  if (!onBillingPage && cs.locked && cs.type) {
    return <AccessWall reason={cs.type} isOwner={isOwner} ws={ws} />;
  }
  if (!onBillingPage && subLocked) {
    return <AccessWall reason="subscription" isOwner={isOwner} ws={ws} />;
  }

  if (cs.active && cs.type) {
    return (
      <>
        <div className={styles.compBanner} role="status">
          <Icon name="zap" size={14} />
          <span>
            {compTypeLabel(cs.type)} ends in {humanizeRemaining(cs.remainingMs)}
          </span>
        </div>
        {children}
      </>
    );
  }

  return <>{children}</>;
}

/**
 * Full-page lock wall shown once a demo/trial has expired OR a subscription
 * has been canceled. The owner CTA points at the in-app billing page
 * (`/w/:ws/billing`), which the API lock allowlist keeps reachable so the
 * owner can subscribe / re-subscribe and restore access.
 */
function AccessWall({
  reason,
  isOwner,
  ws,
}: {
  reason: "demo" | "trial" | "subscription";
  isOwner: boolean;
  ws: string | undefined;
}) {
  const title =
    reason === "subscription"
      ? "Your subscription has ended"
      : `Your ${reason} has ended`;
  return (
    <div className={styles.compWall}>
      <div className={styles.compWallCard}>
        <span className={styles.compWallIcon} aria-hidden="true">
          <Icon name="lock" size={26} />
        </span>
        <h1 className={styles.compWallTitle}>{title}</h1>
        {isOwner ? (
          <>
            <p className={styles.compWallBody}>
              {reason === "subscription"
                ? "Re-subscribe to restore access to this workspace — your brands, drafts, and settings are all kept."
                : "Subscribe to a plan to restore access to this workspace — your brands, drafts, and settings are all kept."}
            </p>
            <Btn href={ws ? `/w/${ws}/billing` : "/pricing"} variant="primary" size="lg">
              Subscribe to continue
            </Btn>
          </>
        ) : (
          <p className={styles.compWallBody}>
            Ask a workspace owner to subscribe to restore access.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Persistent left navigation, matching the Claude Design AppShell SideNav
 * (DESIGN_SPEC §3.3 + docs/design-mockups/queue-shell.jsx). Renders whenever
 * a workspace is in context. Three stable sections — Workspace, Brand, and
 * Account — that don't appear/disappear as you move between pages: the
 * brand-scoped items follow `activeBrand` (URL brand, else last-viewed, else
 * first brand), so opening a workspace-level page like Members or Settings no
 * longer collapses the menu. Count badges + the usage meter are data-driven,
 * so they show placeholders until the backing data is wired (no mock numbers).
 */
function SideNav({
  ws,
  brand,
  activeBrand,
  activeBrandName,
  pathname,
  brands,
  canManageBrands,
  onCreateBrand,
}: {
  ws: string;
  /** Brand in the URL (drives the brand-list highlight), if any. */
  brand?: string | undefined;
  /** Brand the brand-scoped section points at (URL ?? last ?? first). */
  activeBrand?: string | undefined;
  activeBrandName?: string | undefined;
  pathname: string;
  brands: BrandSummary[];
  canManageBrands: boolean;
  onCreateBrand: () => void;
}) {
  const bp = activeBrand ? `/w/${ws}/b/${activeBrand}` : null;

  // Unread daily-digest count drives the badge on the Trends nav item. The
  // hook is enabled only when both ws and brand are set, so with no brand in
  // context it simply returns no data (badge undefined → nothing renders).
  const digestsQuery = useTrendDigests(ws, activeBrand);
  const unreadDigests = (digestsQuery.data?.digests ?? []).filter((d) => !d.readAt).length;

  return (
    <aside className={styles.sideNav}>
      <nav className={styles.navScroll}>
        <div className={styles.navGroup}>
          <div className={styles.navGroupHead}>
            <span className={styles.navGroupLabel}>Workspace</span>
            {canManageBrands && (
              <button
                type="button"
                className={styles.navGroupAction}
                onClick={onCreateBrand}
                aria-label="New brand"
                title="New brand"
              >
                <span aria-hidden="true">+</span>
              </button>
            )}
          </div>
          {brands.map((b) => (
            <NavBrandItem
              key={b.id}
              to={`/w/${ws}/b/${b.id}`}
              name={b.name}
              active={b.id === brand}
            />
          ))}
          {bp && (
            <NavItem
              to={`${bp}/trends`}
              icon="arrow-up-right"
              label="Trends"
              pathname={pathname}
              badge={unreadDigests}
            />
          )}
          {bp && <NavItem to={`${bp}/calendar`} icon="calendar" label="Calendar" pathname={pathname} />}
          {bp && <NavItem to={`${bp}/queue`} icon="bell" label="Approval queue" pathname={pathname} />}
          {bp && <NavItem to={`${bp}/learning`} icon="sparkles" label="Learning" pathname={pathname} />}
          {bp && <NavItem to={`${bp}/media`} icon="image" label="Media Library" pathname={pathname} />}
        </div>
        {bp && (
          <div className={styles.navGroup}>
            <div className={styles.navGroupLabel} title={activeBrandName}>
              {activeBrandName ?? "Brand"}
            </div>
            <NavItem to={`${bp}/edit`} icon="zap" label="Brand brain" pathname={pathname} />
            <NavItem to={`${bp}/inputs`} icon="edit" label="Inputs" pathname={pathname} />
            <NavItem to={`${bp}/products`} icon="image" label="Products" pathname={pathname} />
            <NavItem to={`${bp}/accounts`} icon="link" label="Social accounts" pathname={pathname} />
          </div>
        )}
        <div className={styles.navGroup}>
          <div className={styles.navGroupLabel}>Account</div>
          <NavItem to={`/w/${ws}/members`} icon="user" label="Members" pathname={pathname} />
          <NavItem to={`/w/${ws}/sso`} icon="lock" label="SSO" pathname={pathname} />
          <NavItem to={`/w/${ws}/settings`} icon="edit" label="Settings" pathname={pathname} />
          <NavItem to="/account" icon="smartphone" label="Account" pathname={pathname} />
          <NavItem to={`/w/${ws}/billing`} icon="zap" label="Billing & plan" pathname={pathname} />
        </div>
      </nav>
      {/* SCAFFOLD: usage meter reflects real draft quota once billing usage
          is wired (GET /api/workspaces/:ws usage). No mock numbers. */}
      <div className={styles.usage}>
        <div className={styles.usageLabel}>Drafts used · — / —</div>
        <div className={styles.usageTrack}>
          <div className={styles.usageFill} style={{ width: "0%" }} />
        </div>
      </div>
    </aside>
  );
}

function NavItem({
  to,
  icon,
  label,
  pathname,
  exact,
  badge,
}: {
  to: string;
  icon: IconName;
  label: string;
  pathname: string;
  exact?: boolean;
  /** Optional count pill rendered at the right edge. Hidden when 0/undefined. */
  badge?: number | undefined;
}) {
  const active = exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link to={to} className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}>
      <Icon name={icon} size={15} />
      <span className={styles.navLabel}>{label}</span>
      {typeof badge === "number" && badge > 0 && (
        <span className={styles.navBadge}>{badge}</span>
      )}
    </Link>
  );
}

/**
 * A brand row in the sidebar "Workspace" group: a tinted square avatar with
 * the brand's initials + the brand name, linking into the brand. Active
 * when it's the brand currently in context.
 */
function NavBrandItem({
  to,
  name,
  active,
}: {
  to: string;
  name: string;
  active: boolean;
}) {
  return (
    <Link to={to} className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}>
      <span className={styles.brandAvatar} aria-hidden="true">
        {brandInitials(name)}
      </span>
      <span className={styles.navLabel}>{name}</span>
    </Link>
  );
}
